import BigNumber from 'bignumber.js';
import { parseISO } from 'date-fns';
import isIBAN from 'validator/lib/isIBAN';
import { SDI_FINANCIAL_INSTITUTION_LIMIT } from '~/consts/invoices';
import { EventProps } from '~/hooks/segment';
import { Company, FiscalRegime } from '~/types/company';
import { IdentityType } from '~/types/company-identity';
import { InvoicingSettings } from '~/types/company-settings';
import { Document, DocumentDirection, DocumentType, DocumentVATCollection, EInvoiceType } from '~/types/document';
import {
  DiscountType,
  DocumentBuilderFlowForm,
  DocumentBuilderForm,
  DocumentBuilderLine,
  DocumentDataLine,
  PaymentMethodForm,
  RelatedDocumentLine,
  RelatedDocumentType,
  SocialSecurityForm,
  Stamp,
  TaxableMode,
  TransportInformations,
  WithholdingTaxForm,
} from '~/types/document-builder';
import { FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import {
  CedentePrestatore,
  CessionarioCommittente,
  CondizioniPagamento,
  DatiCassaPrevidenziale,
  DatiDDT,
  DatiGeneraliDocumento,
  DatiPagamento,
  DatiRiepilogo,
  DatiRitenuta,
  DatiTrasmissione,
  DatiTrasporto,
  DettaglioLinea,
  DettaglioPagamento,
  EInvoicePaymentMethod,
  EsigibilitaIva,
  FatturaBody,
  FatturaHeader,
  ItalianEInvoice,
  RiferimentoDocumento,
  StampType,
  TipoScontoMaggiorazione,
  VatTotal,
} from '~/types/invoicing';
import { Money } from '~/types/money';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { defaultDestinationCode } from '~/utils/counterparts';
import { isSelfInvoiceTD26 } from '~/utils/document';
import { parseDocumentFlow } from '~/utils/flow';
import { formatDateForAPI } from '~/utils/formatDate';
import { defaultEInvoiceType, draftTotals, SDI_code, splitMotivation } from '~/utils/invoicing';
import { isManagementDataStamp, lineGrossTotals, lineNetTotals } from '~/utils/invoicing/items';
import {
  serializeRelatedDocument,
  serializeRelatedDocumentForDatiDdt,
  serializeTransportInformations,
} from '~/utils/invoicing/serializers';
import { valueToSocialSecurityEinvoice } from '~/utils/invoicing/social-security';
import { calcWithholdingAmount } from '~/utils/invoicing/withholding';
import { formGrossPrice } from '~/utils/items';
import { ensureDecimalPlaces, newMoney, subtractMoney, sumMoney } from '~/utils/money';
import { limitDecimalPlaces } from '~/utils/numberInputs';
import { cleanCharacters } from '~/utils/parsing';
import { latin1Schema, lineSchema } from '~/utils/parsing';
import { resolveVatExemption, transformVatToBase100 } from '~/utils/vat';
import {
  createDefaultForm,
  createDefaultItems,
  createDefaultPaymentInfo,
  createDefaultStamp,
  createEmptyFlow,
  defaultDiscount,
  defaultTransportInformations,
} from './document-builder';

function serializeDatiTrasmissione(values: DocumentBuilderForm): DatiTrasmissione {
  const counterpart = values.counterpart;
  return {
    progressivoInvio: null,
    formatoTrasmissione: counterpart?.identityType === IdentityType.Pa ? 'FPA12' : 'FPR12',
    codiceDestinatario: destinationCode(values),
    idTrasmittente: null,
    pecDestinatario: counterpart?.certifiedEmail ?? null,
    contattiTrasmittente: {
      email: null,
      telefono: null,
    },
  };
}

function serializeFatturaElettronicaHeader(company: Company, values: DocumentBuilderForm): FatturaHeader {
  return {
    cessionarioCommittente: cessionarioCommittente(company, values),
    cedentePrestatore: cedentePrestatore(company, values),
    terzoIntermediarioOSoggettoEmittente: null,
    datiTrasmissione: serializeDatiTrasmissione(values),
    soggettoEmittente: null,
  };
}

function serializeDatiRitenuta(item: WithholdingTaxForm) {
  return {
    aliquotaRitenuta: item.percentage,
    causalePagamento: item.motivation,
    importoRitenuta: calcWithholdingAmount(item),
    tipoRitenuta: item.type,
    taxableAmount: BigNumber(item.taxableAmount).decimalPlaces(2).toFixed(),
    taxableMode: item.taxableMode,
  };
}

function serializeDatiGeneraliDocumento(
  values: DocumentBuilderForm,
  documentNumber: Document['number'],
  tipoDocumento: EInvoiceType,
  documentTotal: Money,
  vatTotals: VatTotal[],
  stampTotal: Money,
): DatiGeneraliDocumento {
  const motivation = latin1Schema.parse(values.motivation ?? '');

  const importoTotaleDocumento = BigNumber(documentTotal.amount).plus(stampTotal.amount).toFixed(2);
  return {
    arrotondamento: null,
    art73: null,
    data: formatDateForAPI(values.date),
    causale: splitMotivation(motivation),
    divisa: values.currency,
    numero: documentNumber,
    tipoDocumento,
    datiRitenuta: values.withholdingTax.map((x) => serializeDatiRitenuta(x)),
    scontoMaggiorazione: [],
    importoTotaleDocumento,
    datiBollo: values.stamp ? { bolloVirtuale: 'SI', importoBollo: values.stamp.value } : null,
    datiCassaPrevidenziale: valueToSocialSecurityEinvoice(values, vatTotals),
  };
}

function serializeDatiPagamento(
  flow: DocumentBuilderFlowForm,
  numberOfFlows: number,
  paymentInfo: PaymentMethodForm,
): DatiPagamento {
  return {
    condizioniPagamento:
      flow.paymentStatus === FlowPaymentStatus.Paid
        ? CondizioniPagamento.PrePayment
        : numberOfFlows > 1
          ? CondizioniPagamento.Installments
          : CondizioniPagamento.SinglePayment,
    dettaglioPagamento: [
      {
        remoteId: null,
        accountId: null,
        iban: isIBAN(paymentInfo.paymentAccount ?? '') ? (paymentInfo.paymentAccount ?? null) : null,
        modalitaPagamento: parsePaymentMethodEInvoice(paymentInfo.paymentMethod),
        importoPagamento: flow.amount,
        dataScadenzaPagamento: formatDateForAPI(flow.expectedPaymentDate),
        istitutoFinanziario: paymentInfo.institutionName?.substring(0, SDI_FINANCIAL_INSTITUTION_LIMIT) ?? '',
      },
    ],
  };
}

function serializeDatiRiepilogo(vatTotal: VatTotal, vatCollection: EsigibilitaIva | null): DatiRiepilogo {
  const imponibileImporto = BigNumber(vatTotal.netTotal.amount)
    .plus(vatTotal.rounding ?? 0)
    .toFixed();

  return {
    aliquotaIva: BigNumber(vatTotal.vatPercentage).decimalPlaces(2).toFixed(2),
    arrotondamento: null,
    esigibilitaIva: vatCollection,
    imponibileImporto,
    imposta: vatTotal.vatTotal.amount,
    natura: vatTotal.vatExemption,
    riferimentoNormativo: vatTotal.lawReference,
    speseAccessorie: null,
  };
}

function serializeDettaglioLinea(
  item: DocumentBuilderLine,
  index: number,
  includePrices: boolean | null,
  useGrossPrices: boolean,
) {
  const {
    code,
    articleCodes,
    description,
    extendedDescription,
    discount,
    grossPrice,
    netPrice,
    quantity,
    unit,
    managementData,
    vat: { percentage: vatPercentage, taxExemption },
  } = lineSchema.parse(item);

  const totals = useGrossPrices ? lineGrossTotals(item) : lineNetTotals(item);

  const aliquotaIva = vatPercentage.decimalPlaces(2).toFixed(2);

  return {
    aliquotaIva,
    altriDatiGestionali: managementData.map((item) => ({
      tipoDato: item.dataType ?? '',
      riferimentoTesto: item.text ?? null,
      riferimentoNumero: item.number ?? null,
      riferimentoData: item.date ? formatDateForAPI(item.date) : null,
    })),
    codiceArticolo: [
      ...(code ? [{ codiceTipo: 'Codice', codiceValore: code }] : []),
      ...articleCodes.map((articleCode) => ({
        codiceTipo: articleCode.type,
        codiceValore: articleCode.value,
      })),
    ],
    dataFinePeriodo: null,
    dataInizioPeriodo: null,
    descrizione: description,
    extendedDescription,
    grossPrice: grossPrice.toFixed(),
    natura: resolveVatExemption(aliquotaIva, taxExemption),
    riferimentoNormativo: item.vat.lawReference,
    numeroLinea: (index + 1).toString(),
    includiPrezzo: includePrices,
    prezzoTotale: totals.netTotal,
    prezzoUnitario: netPrice.toFixed(),
    quantita: item.quantity,
    riferimentoAmministrazione: null,
    ritenuta: null,
    scontoMaggiorazione: discount.value.isZero()
      ? []
      : [
          {
            tipo: TipoScontoMaggiorazione.Sconto,
            percentuale: discount.type === DiscountType.Percentage ? discount.value.toFixed() : null,
            importo:
              discount.type === DiscountType.Amount && !quantity.isZero()
                ? BigNumber(totals.netDiscountAmount).dividedBy(quantity).decimalPlaces(8).toFixed()
                : null,
          },
        ],
    unitaMisura: unit,
  };
}

function serializeFatturaElettronicaBody(
  values: DocumentBuilderForm,
  documentNumber: Document['number'],
  tipoDocumento: EInvoiceType,
): FatturaBody[] {
  const totals = draftTotals(values);
  return [
    {
      datiGenerali: {
        datiGeneraliDocumento: serializeDatiGeneraliDocumento(
          values,
          documentNumber,
          tipoDocumento,
          totals.documentTotal,
          totals.vatTotals,
          totals.stampTotal,
        ),
        datiOrdineAcquisto: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.OrdineAcquisto)
          .map(serializeRelatedDocument),
        datiContratto: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.Contratto)
          .map(serializeRelatedDocument),
        datiConvenzione: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.Convenzione)
          .map(serializeRelatedDocument),
        datiRicezione: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.Ricezione)
          .map(serializeRelatedDocument),
        datiFattureCollegate: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.FatturaCollegata)
          .map(serializeRelatedDocument),
        fatturaPrincipale: null,
        datiDdt: values.relatedDocuments
          .filter((d) => d.type === RelatedDocumentType.Ddt)
          .map(serializeRelatedDocumentForDatiDdt),
        datiTrasporto: values.transportInformations
          ? serializeTransportInformations(values.transportInformations)
          : null,
        useGrossPrices: values.useGrossPrices,
      },
      datiPagamento: values.flows.map((flow, _, flows) =>
        serializeDatiPagamento(flow, flows.length, values.paymentInfo),
      ),
      datiBeniServizi: {
        datiRiepilogo: totals.vatTotals.map((vatTotal) => serializeDatiRiepilogo(vatTotal, values.vatCollection)),
        dettaglioLinee: values.items.map((item, index) =>
          serializeDettaglioLinea(item, index, values.includePrices, values.useGrossPrices),
        ),
      },
    },
  ];
}

export function valuesToEInvoice(
  company: Company,
  values: DocumentBuilderForm,
  documentNumber: Document['number'],
): ItalianEInvoice {
  const defaultType = defaultEInvoiceType(values.type);
  const subTypeOrDefaultType = values.subtype ?? defaultType;
  const tipoDocumento = subTypeOrDefaultType ?? EInvoiceType.Fattura;

  return {
    versione: values.counterpart?.identityType === IdentityType.Pa ? 'FPA12' : 'FPR12',
    fatturaElettronicaHeader: serializeFatturaElettronicaHeader(company, values),
    fatturaElettronicaBody: serializeFatturaElettronicaBody(values, documentNumber, tipoDocumento),
  };
}

export function destinationCode(values: DocumentBuilderForm) {
  const { type, subtype } = values;
  if (
    (subtype &&
      [
        EInvoiceType.AutofatturaReverseCharge,
        EInvoiceType.AutofatturaServiziEsteri,
        EInvoiceType.AutofatturaBeniEU,
        EInvoiceType.AutofatturaBeniEsteri,
        EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili,
        EInvoiceType.AutofatturaPerAutoconsumo,
      ].includes(subtype)) ||
    (type === DocumentType.SelfInvoice && subtype === EInvoiceType.Fattura)
  ) {
    return SDI_code;
  }

  const counterpart = values.counterpart;

  const currentDestinationCode = counterpart?.destinationCode ?? null;
  const country = counterpart?.country ?? 'IT';
  const identityType = counterpart?.identityType ?? IdentityType.Company;

  return defaultDestinationCode(currentDestinationCode, country, identityType);
}

function cessionarioCommittente(company: Company, values: DocumentBuilderForm): CessionarioCommittente {
  const { counterpart, type } = values;
  const { companyIdentity } = company;
  if (type === DocumentType.SelfInvoice) {
    return {
      sede: {
        cap: companyIdentity.country !== 'IT' ? '00000' : (companyIdentity.postalCode ?? ''),
        nazione: companyIdentity.addressCountry ?? '',
        indirizzo: companyIdentity.address ?? '',
        comune: companyIdentity.city ?? '',
        provincia: companyIdentity.country !== 'IT' ? '' : (companyIdentity.provinceCode ?? ''),
        numeroCivico: '',
      },
      datiAnagrafici: {
        anagrafica: {
          denominazione: companyIdentity.companyName ?? company.name,
          nome: null,
          cognome: null,
          titolo: null,
          codEori: null,
        },
        idFiscaleIva: {
          idPaese: company.country,
          idCodice: companyIdentity.vatNumber ?? company.vatNumber ?? '',
        },
        codiceFiscale: companyIdentity.taxNumber,
      },
    };
  }
  return {
    sede: {
      cap: counterpart?.country !== 'IT' ? '00000' : (counterpart.postalCode?.trim() ?? ''),
      nazione: counterpart?.addressCountry ?? '',
      indirizzo: counterpart?.address ?? '',
      comune: counterpart?.city ?? '',
      provincia: counterpart?.country !== 'IT' ? '' : (counterpart.provinceCode ?? ''),
      numeroCivico: '',
    },
    datiAnagrafici: {
      anagrafica: {
        denominazione: counterpart?.companyName?.trim() ?? '',
        nome: null,
        cognome: null,
        titolo: null,
        codEori: null,
      },
      idFiscaleIva: counterpart?.vatNumber
        ? {
            idPaese: counterpart.country,
            idCodice: counterpart.vatNumber,
          }
        : null,
      codiceFiscale: counterpart?.taxNumber ?? null,
    },
  };
}

function cedentePrestatore(company: Company, values: DocumentBuilderForm): CedentePrestatore {
  const { counterpart, type } = values;
  const { companyIdentity } = company;
  if (type === DocumentType.SelfInvoice) {
    return {
      contatti: null,
      sede: {
        cap: counterpart?.country !== 'IT' ? '00000' : (counterpart.postalCode?.trim() ?? ''),
        nazione: counterpart?.addressCountry ?? '',
        indirizzo: counterpart?.address ?? '',
        comune: counterpart?.city ?? '',
        provincia: counterpart?.country !== 'IT' ? '' : (counterpart.provinceCode ?? ''),
        numeroCivico: null,
      },
      datiAnagrafici: {
        anagrafica: {
          denominazione: counterpart?.companyName?.trim() ?? '',
          nome: null,
          cognome: null,
          titolo: null,
          codEori: null,
        },
        idFiscaleIva: {
          idPaese: counterpart?.country ?? '',
          idCodice: counterpart?.vatNumber ?? '',
        },
        codiceFiscale: counterpart?.taxNumber ?? null,
        regimeFiscale: FiscalRegime.RF01,
        alboProfessionale: null,
        provinciaAlbo: null,
        numeroIscrizioneAlbo: null,
        dataIscrizioneAlbo: null,
      },
      riferimentoAmministrazione: values.riferimentoAmministrazione ?? null,
    };
  }
  return {
    contatti: null,
    sede: {
      cap: companyIdentity.country !== 'IT' ? '00000' : (companyIdentity.postalCode?.trim() ?? ''),
      nazione: companyIdentity.addressCountry ?? '',
      indirizzo: companyIdentity.address ?? '',
      comune: companyIdentity.city ?? '',
      provincia: companyIdentity.country !== 'IT' ? '' : (companyIdentity.provinceCode ?? ''),
      numeroCivico: null,
    },
    datiAnagrafici: {
      anagrafica: {
        denominazione: companyIdentity.companyName ?? company.name,
        nome: null,
        cognome: null,
        titolo: null,
        codEori: null,
      },
      idFiscaleIva: {
        idPaese: company.country,
        idCodice: companyIdentity.vatNumber ?? company.vatNumber ?? '',
      },
      codiceFiscale: companyIdentity.taxNumber,
      regimeFiscale: company.fiscalRegime ?? FiscalRegime.RF01,
      alboProfessionale: null,
      provinciaAlbo: null,
      numeroIscrizioneAlbo: null,
      dataIscrizioneAlbo: null,
    },
    riferimentoAmministrazione: values.riferimentoAmministrazione ?? null,
  };
}

function defineStampType(body: FatturaBody, items: DocumentBuilderLine[]): Stamp | null {
  const found = items.find((item) => item.managementData.find(isManagementDataStamp));

  if (body.datiGenerali.datiGeneraliDocumento.datiBollo?.importoBollo && !found) {
    return { type: StampType.Issuer, value: body.datiGenerali.datiGeneraliDocumento.datiBollo.importoBollo };
  }

  if (found) {
    return { type: StampType.Customer, value: found.netPrice };
  }

  return null;
}

export const getDocumentType = (
  datiGeneraliDocumento: DatiGeneraliDocumento,
  document: Document | undefined,
  documentType: DocumentType | undefined,
): DocumentType => {
  switch (true) {
    // Self invoice TD26
    case isSelfInvoiceTD26(documentType, datiGeneraliDocumento.tipoDocumento):
      return DocumentType.SelfInvoice;
    case documentType === DocumentType.Invoice &&
      datiGeneraliDocumento.tipoDocumento === EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili:
      return DocumentType.Invoice;
    // Self invoice TD01
    case document &&
      datiGeneraliDocumento.tipoDocumento === EInvoiceType.Fattura &&
      document.direction === DocumentDirection.received:
      return DocumentType.SelfInvoice;
    default:
      return documentType ? documentType : parseEInvoiceType(datiGeneraliDocumento.tipoDocumento);
  }
};

const fillLawReference = (
  items: DocumentBuilderLine[] | DocumentDataLine[],
  datiRiepilogo: DatiRiepilogo[],
): DocumentBuilderLine[] | DocumentDataLine[] => {
  const mapping = new Map(datiRiepilogo.map((x) => [x.natura ?? '_', x.riferimentoNormativo]));
  // If all the items don't have a lawReference but in the mapping there is at least one
  // law reference we use it to fill the items
  if (
    items.every((i) => (i.vat.lawReference ?? '') === '') &&
    [...mapping.values()].some((value) => (value ?? '') !== '') //don't delete the spread operator because mapping.values don't always returns an iterable and could lead to an error
  ) {
    items.forEach((x) => (x.vat.lawReference = mapping.get(x.vat.taxExemption ?? '_') ?? null));
  }
  return items;
};

export const eInvoiceFromInvoiceToForm = (
  invoice: ItalianEInvoice,
  documentType: DocumentType | undefined,
  // Needed for retro compatibility, it is the local storage value saved in the atom
  useGrossPricesState: boolean,
  document?: Document,
  fiscalRegime?: FiscalRegime | null,
  paymentInfo?: PaymentMethodForm,
): DocumentBuilderForm => {
  const body = invoice.fatturaElettronicaBody[0]!;
  const datiGenerali = body.datiGenerali;
  const datiGeneraliDocumento = datiGenerali.datiGeneraliDocumento;
  const useGrossPrices = datiGenerali.useGrossPrices ?? useGrossPricesState;
  const items = body.datiBeniServizi.dettaglioLinee.map((item) => {
    const { discounts, ...rest } = parseEInvoiceItem(item);

    let discount = discounts.length > 0 ? discounts[0]! : defaultDiscount;
    if (useGrossPrices && discount.type === DiscountType.Amount && discounts.length > 0) {
      const vatPercentage = transformVatToBase100(BigNumber(item.aliquotaIva));
      //not using amount from parseEInvoiceItem because it's rounded
      const rawImporto =
        item.scontoMaggiorazione.find((s) => s.tipo === TipoScontoMaggiorazione.Sconto && s.importo !== null)
          ?.importo ?? discount.value;
      discount = {
        type: discount.type,
        value: BigNumber(rawImporto)
          .multipliedBy(item.quantita ?? '1')
          .multipliedBy(BigNumber(1).plus(vatPercentage.dividedBy(100)))
          .decimalPlaces(2)
          .toFixed(),
      };
    }

    return { ...rest, discount };
  }) as DocumentBuilderLine[];

  const itemsWithLawReference = fillLawReference(items, body.datiBeniServizi.datiRiepilogo) as DocumentBuilderLine[];

  assertIsDefined(body.datiBeniServizi.dettaglioLinee[0], 'there must be at least one dettaglio linee');
  const includePrices = body.datiBeniServizi.dettaglioLinee[0].includiPrezzo;
  const stamp = defineStampType(body, items);
  const totalAmounts = body.datiBeniServizi.dettaglioLinee.reduce(
    (acc, curr) => {
      // Lines with N1 nature codes ("Escluse ex art. 15", spese anticipate) should
      // be excluded from total amounts calculations. See TS-999.
      if (curr.natura === 'N1') {
        return acc;
      }

      const currentNetAmount = BigNumber(curr.prezzoTotale);
      // Accumulate the total net amount of all the element
      acc.totalNetAmount = acc.totalNetAmount.plus(currentNetAmount);

      // Accumulate the total net amount of the element that are not a "Bollo"
      const hasBollo = curr.altriDatiGestionali.some((e) => e.tipoDato === 'Bollo');
      if (!hasBollo) {
        acc.totalNetAmountWithoutStamp = acc.totalNetAmountWithoutStamp.plus(currentNetAmount);
      }

      return acc;
    },
    {
      totalNetAmount: BigNumber(0),
      totalNetAmountWithoutStamp: BigNumber(0),
    },
  );

  const { totalNetAmount, totalNetAmountWithoutStamp } = totalAmounts;

  const paymentInfoComputed = paymentInfo ?? {
    accountId: null,
    institutionName: null,
    paymentMethod: PaymentMethod.Transfer,
    paymentAccount: '',
  };

  return {
    attachmentsSize: 0,
    categoryId: document?.category?.id ?? null,
    counterpart: document?.counterpart ?? null,
    currency: datiGeneraliDocumento.divisa,
    flows:
      document?.flows && document.flows.length > 0
        ? [...document.flows]
            .sort((a, b) => a.expectedPaymentDate.localeCompare(b.expectedPaymentDate))
            .map(parseDocumentFlow)
        : body.datiPagamento.flatMap((d) =>
            d.dettaglioPagamento.map((x) => parseEInvoiceFlow(x, d.condizioniPagamento)),
          ),
    date: parseISO(datiGeneraliDocumento.data),
    fiscalRegime: fiscalRegime ?? FiscalRegime.RF01,
    isEInvoice: isDefined(document) ? document.isEInvoice : true,
    includePrices: documentType === DocumentType.DeliveryNote ? includePrices : null,
    items: itemsWithLawReference,
    motivation: datiGeneraliDocumento.causale?.join('') ?? '',
    notes: document?.notes || null,
    paymentInfo: body.datiPagamento[0]?.dettaglioPagamento[0]
      ? parseEInvoicePaymentInfo(body.datiPagamento[0].dettaglioPagamento[0])
      : paymentInfoComputed,
    relatedDocuments: [
      ...datiGenerali.datiOrdineAcquisto.map((doc) =>
        parseRelatedDocument(doc, RelatedDocumentType.OrdineAcquisto, document),
      ),
      ...datiGenerali.datiContratto.map((doc) => parseRelatedDocument(doc, RelatedDocumentType.Contratto)),
      ...datiGenerali.datiConvenzione.map((doc) => parseRelatedDocument(doc, RelatedDocumentType.Convenzione)),
      ...datiGenerali.datiRicezione.map((doc) => parseRelatedDocument(doc, RelatedDocumentType.Ricezione)),
      ...(datiGenerali.datiFattureCollegate ?? []).map((doc) =>
        parseRelatedDocument(doc, RelatedDocumentType.FatturaCollegata, document),
      ),
      ...datiGenerali.datiDdt.map((doc) => parseDatiDdtToRelatedDocument(doc, document)),
    ],
    subcategoryId: document?.subcategory?.id ?? null,
    type: getDocumentType(datiGeneraliDocumento, document, documentType),
    subtype: datiGeneraliDocumento.tipoDocumento,
    vatCollection: body.datiBeniServizi.datiRiepilogo[0]?.esigibilitaIva ?? null,
    withholdingTax: datiGeneraliDocumento.datiRitenuta.map((dR) => {
      return parseWithholding(dR, totalNetAmountWithoutStamp); // use total without stamp because is not part of the whithold
    }),
    socialSecurity: datiGeneraliDocumento.datiCassaPrevidenziale[0]
      ? parseSocialSecurity(datiGeneraliDocumento.datiCassaPrevidenziale[0], totalNetAmount)
      : null,
    stamp,
    riferimentoAmministrazione: invoice.fatturaElettronicaHeader.cedentePrestatore.riferimentoAmministrazione ?? null,
    transportInformations: datiGenerali.datiTrasporto
      ? parseTransportInformation(datiGenerali.datiTrasporto)
      : documentType === DocumentType.DeliveryNote
        ? defaultTransportInformations
        : null,
    useGrossPrices,
  };
};

const eInvoiceFromDocumentToForm = (
  document: Document,
  fiscalRegime: FiscalRegime | null | undefined,
  { payment, vat, stamp }: InvoicingSettings,
  // Needed for retro compatibility, it is the local storage value saved in the atom
  useGrossPricesState: boolean,
) => {
  return {
    attachmentsSize: 0,
    categoryId: document.category?.id ?? null,
    counterpart: document.counterpart ?? null,
    currency: 'EUR',
    date: parseISO(document.creationDate),
    fiscalRegime: fiscalRegime ?? FiscalRegime.RF01,
    flows:
      document.flows && document.flows.length > 0
        ? document.flows.map(parseDocumentFlow)
        : [createEmptyFlow(parseISO(document.creationDate), payment, document.counterpart)],
    includePrices: null,
    items: createDefaultItems({ ...vat, lawReference: null }, stamp, fiscalRegime),
    isEInvoice: document.isEInvoice,
    lawReference: {},
    motivation: '',
    notes: document.notes || '',
    paymentInfo: {
      accountId: document.flows?.[0]?.account?.id ?? null,
      institutionName: document.flows?.[0]?.accountInstitution ?? null,
      paymentMethod: document.flows?.[0]?.paymentMethod ?? PaymentMethod.Transfer,
      paymentAccount: document.flows?.[0]?.paymentAccount ?? '',
    },
    relatedDocuments: [],
    subcategoryId: document.subcategory?.id ?? null,
    subtype: defaultEInvoiceType(document.documentType),
    type: document.documentType,
    vatCollection: parseDocumentVatCollection(document.vatCollection),
    withholdingTax: [],
    socialSecurity: null,
    riferimentoAmministrazione: null,
    stamp: createDefaultStamp(stamp),
    transportInformations: defaultTransportInformations,
    useGrossPrices: useGrossPricesState,
  };
};

export const createEmptyBuilderFrom = (
  invoicingSettings: InvoicingSettings,
  fiscalRegime: FiscalRegime | undefined | null,
  // Needed for retro compatibility, it is the local storage value saved in the atom
  useGrossPricesState = false,
): DocumentBuilderForm => {
  return {
    ...createDefaultForm(fiscalRegime, invoicingSettings, useGrossPricesState),
    transportInformations: defaultTransportInformations,
  };
};

export const eInvoiceToForm = (
  invoicingSettings: InvoicingSettings,
  fiscalRegime: FiscalRegime | undefined | null,
  document: Document | undefined,
  invoice: ItalianEInvoice | undefined,
  documentType: DocumentType | undefined,
  // Needed for retro compatibility, it is the local storage value saved in the atom
  useGrossPricesState: boolean,
): DocumentBuilderForm => {
  if (invoice) {
    return eInvoiceFromInvoiceToForm(
      invoice,
      documentType,
      useGrossPricesState,
      document,
      fiscalRegime,
      createDefaultPaymentInfo(invoicingSettings.payment),
    );
  }

  if (document) {
    return eInvoiceFromDocumentToForm(document, fiscalRegime, invoicingSettings, useGrossPricesState);
  }

  return createEmptyBuilderFrom(invoicingSettings, fiscalRegime, useGrossPricesState);
};

const getGrossPriceItem = (item: DettaglioLinea, vatPercentage: string) => {
  const isBollo = item.altriDatiGestionali.some((dati) => dati.tipoDato === 'Bollo');

  if (item.grossPrice) {
    return isBollo && BigNumber(item.grossPrice).isZero()
      ? BigNumber(2).toFixed() // CS-3557
      : BigNumber(item.grossPrice).toFixed();
  }

  return formGrossPrice(item.prezzoUnitario, vatPercentage);
};

export const isNonEmptyEInvoiceLine = (line: DettaglioLinea): boolean => {
  const prezzoTotale = BigNumber(line.prezzoTotale);
  const prezzoUnitario = BigNumber(line.prezzoUnitario);
  const quantita = BigNumber(line.quantita ?? '0');

  return !prezzoTotale.eq(0) || !prezzoUnitario.eq(0) || quantita.gt(0);
};

const formatManagementDataNumber = (value: string | BigNumber): string => {
  const normalizedValue = BigNumber.isBigNumber(value) ? value.toFixed() : value.replace(',', '.');

  if (!normalizedValue.includes('.')) {
    return normalizedValue;
  }

  return normalizedValue.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
};

export function parseEInvoiceItem(item: DettaglioLinea): DocumentDataLine {
  const vatPercentage = transformVatToBase100(BigNumber(item.aliquotaIva)).toFixed();
  const code = item.codiceArticolo.find(({ codiceTipo }) => codiceTipo === 'Codice');
  const articleCodes = item.codiceArticolo.filter(({ codiceTipo }) => codiceTipo !== code?.codiceTipo);

  return {
    code: code?.codiceValore ?? '',
    description: item.descrizione || '',
    extendedDescription: item.extendedDescription || '',
    discounts: getDiscountPercentages(item),
    quantity: item.quantita ? BigNumber(item.quantita).toFixed() : '1',
    unit: item.unitaMisura ?? '',
    netPrice: BigNumber(item.prezzoUnitario).toFixed(),
    grossPrice: getGrossPriceItem(item, vatPercentage),
    vat: {
      lawReference: item.riferimentoNormativo ?? null,
      percentage: vatPercentage,
      taxExemption: resolveVatExemption(vatPercentage, item.natura),
    },
    articleCodes: articleCodes.map((articleCode) => ({
      type: articleCode.codiceTipo,
      value: articleCode.codiceValore,
    })),
    managementData: item.altriDatiGestionali.map((datiGestionali) => ({
      dataType: datiGestionali.tipoDato,
      text: datiGestionali.riferimentoTesto ?? '',
      date: datiGestionali.riferimentoData ? new Date(datiGestionali.riferimentoData) : null,
      number: datiGestionali.riferimentoNumero ? formatManagementDataNumber(datiGestionali.riferimentoNumero) : null,
    })),
  };
}

function parseEInvoicePaymentInfo(paymentInfo: DettaglioPagamento): PaymentMethodForm {
  return {
    accountId: paymentInfo.accountId,
    institutionName: paymentInfo.istitutoFinanziario,
    paymentMethod: parseEInvoicePaymentMethod(paymentInfo.modalitaPagamento),
    paymentAccount: paymentInfo.iban ?? '',
  };
}

function parseEInvoicePaymentMethod(paymentMethod: EInvoicePaymentMethod): PaymentMethod {
  switch (paymentMethod) {
    case EInvoicePaymentMethod.Contanti:
    case EInvoicePaymentMethod.ContantiPressoTesoreria:
      return PaymentMethod.Cash;
    case EInvoicePaymentMethod.Assegno:
    case EInvoicePaymentMethod.AssegnoCircolare:
      return PaymentMethod.Check;
    case EInvoicePaymentMethod.Bonifico:
      return PaymentMethod.Transfer;
    case EInvoicePaymentMethod.CartaDiPagamento:
      return PaymentMethod.Card;
    case EInvoicePaymentMethod.RID:
    case EInvoicePaymentMethod.RIDUtenze:
    case EInvoicePaymentMethod.RIDVeloce:
    case EInvoicePaymentMethod.SEPADirectDebit:
    case EInvoicePaymentMethod.SEPADirectDebitCORE:
    case EInvoicePaymentMethod.SEPADirectDebitB2B:
      return PaymentMethod.Sdd;
    case EInvoicePaymentMethod.RIBA:
      return PaymentMethod.Riba;
    case EInvoicePaymentMethod.BollettinoPostale:
      return PaymentMethod.Postal;
    default:
      return PaymentMethod.Other;
  }
}

function parsePaymentMethodEInvoice(paymentMethod: PaymentMethod): EInvoicePaymentMethod {
  switch (paymentMethod) {
    case PaymentMethod.Transfer:
      return EInvoicePaymentMethod.Bonifico;
    case PaymentMethod.Cash:
      return EInvoicePaymentMethod.Contanti;
    case PaymentMethod.Card:
      return EInvoicePaymentMethod.CartaDiPagamento;
    case PaymentMethod.Check:
      return EInvoicePaymentMethod.Assegno;
    case PaymentMethod.Postal:
      return EInvoicePaymentMethod.BollettinoPostale;
    case PaymentMethod.Riba:
      return EInvoicePaymentMethod.RIBA;
    case PaymentMethod.Sdd:
      return EInvoicePaymentMethod.SEPADirectDebit;
    case PaymentMethod.FiscalCredit:
      return EInvoicePaymentMethod.QuietanzaErario;
  }
  return EInvoicePaymentMethod.Bonifico;
}

export function parseRelatedDocument(
  relatedDocument: RiferimentoDocumento,
  type: RelatedDocumentType,
  document?: Document,
): RelatedDocumentLine {
  const sibillDocument = document?.related.find(
    (d) =>
      (d.number === relatedDocument.idDocumento &&
        d.creationDate === relatedDocument.data &&
        d.documentType === DocumentType.Invoice &&
        type === RelatedDocumentType.FatturaCollegata) ||
      (d.documentType === DocumentType.Quote && type === RelatedDocumentType.OrdineAcquisto),
  );

  return {
    dbDocumentId: sibillDocument?.id ?? null,
    CIGCode: relatedDocument.codiceCig ?? '',
    CUPCode: relatedDocument.codiceCup ?? '',
    date: relatedDocument.data ? parseISO(relatedDocument.data) : null,
    documentNumber: cleanCharacters(relatedDocument.idDocumento),
    orderCode: relatedDocument.codiceCommessaConvenzione ?? '',
    type,
  };
}

function parseDatiDdtToRelatedDocument(transportData: DatiDDT, document?: Document): RelatedDocumentLine {
  const sibillDdt = document?.related.find(
    (d) =>
      d.number === transportData.numeroDdt &&
      d.creationDate === transportData.dataDdt &&
      d.documentType === DocumentType.DeliveryNote,
  );
  return {
    dbDocumentId: sibillDdt?.id ?? null,
    CIGCode: '',
    CUPCode: '',
    date: parseISO(transportData.dataDdt),
    documentNumber: cleanCharacters(transportData.numeroDdt),
    orderCode: '',
    type: RelatedDocumentType.Ddt,
  };
}

function parseEInvoiceType(eInvoiceType: EInvoiceType, documentType?: DocumentType): DocumentType {
  if (eInvoiceType === EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili) {
    if (documentType === DocumentType.Invoice || documentType === DocumentType.SelfInvoice) {
      return documentType;
    }
    return DocumentType.SelfInvoice;
  }

  switch (eInvoiceType) {
    case EInvoiceType.Fattura:
    case EInvoiceType.AccontoFattura:
    case EInvoiceType.FatturaDifferita:
      return DocumentType.Invoice;
    case EInvoiceType.NotaDiCredito:
      return DocumentType.CreditNote;
    case EInvoiceType.NotaDiDebito:
      return DocumentType.DebitNote;
    case EInvoiceType.Parcella:
      return DocumentType.Parcel;
    case EInvoiceType.AutofatturaReverseCharge:
    case EInvoiceType.AutofatturaServiziEsteri:
    case EInvoiceType.AutofatturaBeniEU:
    case EInvoiceType.AutofatturaBeniEsteri:
    case EInvoiceType.CommunicationOmittedIrregularInvoice:
    case EInvoiceType.AutofatturaPerAutoconsumo:
      return DocumentType.SelfInvoice;
  }
}

function parseWithholding(withholding: DatiRitenuta, totalNetAmount: BigNumber): WithholdingTaxForm {
  const aliquotaRitenuta = BigNumber(withholding.aliquotaRitenuta);
  const importoRitenuta = BigNumber(withholding.importoRitenuta);

  const common = {
    type: withholding.tipoRitenuta,
    motivation: withholding.causalePagamento,
    taxableMode: withholding.taxableMode ?? TaxableMode.Percentage,
    percentage: transformVatToBase100(aliquotaRitenuta).toFixed(),
  };

  // Here we check if the importoRitenuta = totalAmount * 100 / aliquotaRitenuta.
  // If so we use totalNetAmount as amount and the percentage is 100% and we avoid
  // Precision errors
  const isFromTotalNetAmount = totalNetAmount
    .multipliedBy(aliquotaRitenuta)
    .dividedBy(100)
    .decimalPlaces(2)
    .eq(importoRitenuta);

  if (isFromTotalNetAmount) {
    return {
      amountPercentage: '100',
      taxableAmount: totalNetAmount.toFixed(),
      ...common,
    };
  }

  // For backward compatibility we must keep the calculation
  const taxableAmount = withholding.taxableAmount
    ? BigNumber(withholding.taxableAmount)
    : importoRitenuta.multipliedBy(100).dividedBy(aliquotaRitenuta);

  const amountPercentage = taxableAmount.dividedBy(totalNetAmount).multipliedBy(100).decimalPlaces(2);

  return {
    amountPercentage: BigNumber.minimum(amountPercentage, 100).toFixed(),
    taxableAmount: taxableAmount.toFixed(),
    ...common,
  };
}

function parseSocialSecurity(socialSecurity: DatiCassaPrevidenziale, totalNetAmount: BigNumber): SocialSecurityForm {
  const imponibileCassa = BigNumber(socialSecurity.imponibileCassa ?? '0');
  const amountPercentage = imponibileCassa.dividedBy(totalNetAmount).multipliedBy(100).decimalPlaces(2).toFixed();

  return {
    amountPercentage,
    percentage: transformVatToBase100(BigNumber(socialSecurity.alCassa)).toFixed(),
    taxableAmount: imponibileCassa.toFixed(),
    type: socialSecurity.tipoCassa,
  };
}

function parseEInvoiceFlow(
  pagamento: DettaglioPagamento,
  condizioniPagamento: CondizioniPagamento,
): DocumentBuilderFlowForm {
  return {
    remoteId: pagamento.remoteId ?? null,
    amount: BigNumber(pagamento.importoPagamento).toFixed(),
    expectedPaymentDate: pagamento.dataScadenzaPagamento ? parseISO(pagamento.dataScadenzaPagamento) : new Date(),
    notes: '',
    paymentStatus:
      condizioniPagamento === CondizioniPagamento.PrePayment ? FlowPaymentStatus.Paid : FlowPaymentStatus.ToPay,
  };
}

function getDiscountPercentages(item: DettaglioLinea): DocumentDataLine['discounts'] {
  const discounts = item.scontoMaggiorazione
    .filter((p) => {
      switch (p.tipo) {
        case TipoScontoMaggiorazione.Sconto:
          return isDefined(p.percentuale) || isDefined(p.importo);
        case TipoScontoMaggiorazione.Maggiorazione:
          return false;
      }
    })
    .map((d) => {
      const discountType = d.percentuale ? DiscountType.Percentage : DiscountType.Amount;
      const discountValue = d.percentuale ?? d.importo;

      return { value: limitDecimalPlaces(discountValue ?? '0', 2), type: discountType };
    });
  return discounts.length > 0 ? discounts : [defaultDiscount];
}

export function parseDocumentVatCollection(vatCollection: DocumentVATCollection | null): EsigibilitaIva | null {
  switch (vatCollection) {
    case DocumentVATCollection.Deferred:
      return EsigibilitaIva.Deferred;
    case DocumentVATCollection.Immediate:
      return EsigibilitaIva.Immediate;
    case DocumentVATCollection.Split:
      return EsigibilitaIva.Split;
  }
  return null;
}

function parseTransportInformation(datiTrasporto: DatiTrasporto): TransportInformations {
  return {
    destination: {
      address: datiTrasporto.indirizzoResa?.indirizzo ?? null,
      postalCode: datiTrasporto.indirizzoResa?.cap ?? null,
      city: datiTrasporto.indirizzoResa?.comune ?? null,
      country: datiTrasporto.indirizzoResa?.nazione ?? null,
    },
    transportReason: datiTrasporto.causaleTrasporto,
    courier: datiTrasporto.datiAnagraficiVettore?.anagrafica.nome ?? null,
    packsNumber: datiTrasporto.numeroColli ? datiTrasporto.numeroColli.toString() : null,
    weight: datiTrasporto.pesoNetto,
  };
}

export function esigibilitaToVatCollection(esigibilita: EsigibilitaIva | null): DocumentVATCollection | null {
  switch (esigibilita) {
    case EsigibilitaIva.Deferred:
      return DocumentVATCollection.Deferred;
    case EsigibilitaIva.Immediate:
      return DocumentVATCollection.Immediate;
    case EsigibilitaIva.Split:
      return DocumentVATCollection.Split;
  }
  return null;
}

export function eInvoiceAnalytics(invoice: Document, einvoice: DocumentBuilderForm): EventProps {
  return {
    documentId: invoice.id,
    documentType: invoice.documentType,
    eInvoiceType: einvoice.subtype ?? defaultEInvoiceType(invoice.documentType),
    isElectronic: invoice.isEInvoice,
    context: 'document-builder',
    lineDiscount: einvoice.items.find((i) => (i.discount.value !== '0' ? i.discount.value : undefined)),
    vatCollection: einvoice.vatCollection,
    hasRitenuta: einvoice.withholdingTax.length > 0,
    hasDDT: einvoice.relatedDocuments.filter((d) => d.type === RelatedDocumentType.Ddt).length > 0,
    hasRelatedDocument: einvoice.relatedDocuments.length > 0,
    hasCausale: (einvoice.motivation ?? '').length > 0,
  };
}

/**
 * Get the related documents from the einvoice
 * @param einvoice - The einvoice to get the related documents from
 * @param document - The document to get the related documents from
 * @returns The related documents
 */
export function getRelatedDocumentsFromEInvoice(einvoice: ItalianEInvoice, document?: Document): RelatedDocumentLine[] {
  return einvoice.fatturaElettronicaBody.flatMap((fattura) =>
    (fattura.datiGenerali.datiFattureCollegate ?? []).map((r) =>
      parseRelatedDocument(r, RelatedDocumentType.FatturaCollegata, document),
    ),
  );
}

export const fromEinvoiceToPreview = (einvoice: ItalianEInvoice, documentType: DocumentType) => {
  const { fatturaElettronicaBody } = einvoice;
  const body = fatturaElettronicaBody[0]!;
  const currency = body.datiGenerali.datiGeneraliDocumento.divisa;
  const type = parseEInvoiceType(body.datiGenerali.datiGeneraliDocumento.tipoDocumento, documentType);

  const socialSecurity = body.datiGenerali.datiGeneraliDocumento.datiCassaPrevidenziale.map((cp) => ({
    alCassa: cp.alCassa,
    imponibileCassa: newMoney(cp.imponibileCassa ?? '0', currency),
    importoContributoCassa: newMoney(cp.importoContributoCassa, currency),
  }))[0];

  const vatTotals = body.datiBeniServizi.datiRiepilogo.map((line) => ({
    taxable: newMoney(line.imponibileImporto, currency),
    vatRate: line.aliquotaIva,
    vatAmount: newMoney(line.imposta, currency),
    vatExemption: line.natura,
    lawReference: line.riferimentoNormativo ?? null,
    shouldBePaid: line.esigibilitaIva !== EsigibilitaIva.Split && type !== DocumentType.SelfInvoice,
  }));

  const netTotal = sumMoney(...body.datiBeniServizi.dettaglioLinee.map((l) => newMoney(l.prezzoTotale, currency)));

  // The payments are in the eInvoice but could be misleading since the eInvoice is decorated with flows when is received
  const payments = body.datiPagamento
    .flatMap((d) => d.dettaglioPagamento.map((x) => parseEInvoiceFlow(x, d.condizioniPagamento)))
    .filter((x) => x.paymentStatus === FlowPaymentStatus.Paid)
    .map((p) => p.amount);

  const alreadyPaidTotal = newMoney(BigNumber.sum(0, ...payments).toFixed(2), currency);

  const vatNotToBePaid: Money = sumMoney(
    { currency, amount: '0' },
    ...vatTotals.filter((l) => !l.shouldBePaid).map((l) => l.vatAmount),
  );

  const { grossTotal: documentTotal } = calculateDocumentTotals(body);

  const withholdingTaxTotal = newMoney(
    BigNumber.sum(
      0,
      ...body.datiGenerali.datiGeneraliDocumento.datiRitenuta.map((r) => BigNumber(r.importoRitenuta)),
    ).toFixed(2),
    currency,
  );

  const totalToPay = ensureDecimalPlaces(
    subtractMoney(subtractMoney(subtractMoney(documentTotal, alreadyPaidTotal), withholdingTaxTotal), vatNotToBePaid),
    2,
  );

  return {
    netTotal,
    socialSecurity,
    vatTotals,
    alreadyPaidTotal,
    documentTotal,
    totalToPay,
  };
};

export const fromEInvoiceDettaglioLineeToItemsPreview = (
  dettaglioLinee: DettaglioLinea[],
  currency: string,
  datiRiepilogo: DatiRiepilogo[],
) => {
  const items = dettaglioLinee.map((line) => {
    const netTotal = newMoney(line.prezzoTotale, currency);
    const grossTotal = newMoney(formGrossPrice(line.prezzoTotale, line.aliquotaIva), currency);
    return {
      value: parseEInvoiceItem(line),
      netTotal,
      grossTotal,
    };
  });

  fillLawReference(
    items.map((i) => i.value),
    datiRiepilogo,
  );

  return items;
};

export enum InoviceRoundingStrategy {
  IGNORE,
  ADD,
  SUBTRACT,
}

export const calculateDocumentTotals = (
  body: FatturaBody,
): { taxableTotal: Money; vatTotal: Money; grossTotal: Money; roundingStrategy: InoviceRoundingStrategy } => {
  const currency = body.datiGenerali.datiGeneraliDocumento.divisa;
  let taxableTotal = body.datiBeniServizi.datiRiepilogo.reduce((acc, dr) => {
    return BigNumber.sum(acc, dr.imponibileImporto);
  }, new BigNumber(0));

  const vatTotal = body.datiBeniServizi.datiRiepilogo.reduce((acc, dr) => {
    return BigNumber.sum(acc, dr.imposta);
  }, new BigNumber(0));

  const roundingTotal = body.datiBeniServizi.datiRiepilogo
    .filter((dr) => dr.arrotondamento !== null)
    .reduce((acc, dr) => {
      return BigNumber.sum(acc, dr.arrotondamento!);
    }, new BigNumber(0));

  const importoTotaleDocumento = body.datiGenerali.datiGeneraliDocumento.importoTotaleDocumento
    ? new BigNumber(body.datiGenerali.datiGeneraliDocumento.importoTotaleDocumento)
    : new BigNumber(0);

  let roundingStrategy = InoviceRoundingStrategy.IGNORE;
  if (taxableTotal.plus(vatTotal).plus(roundingTotal).eq(importoTotaleDocumento)) {
    taxableTotal = taxableTotal.plus(roundingTotal);
    roundingStrategy = InoviceRoundingStrategy.ADD;
  } else if (taxableTotal.plus(vatTotal).minus(roundingTotal).eq(importoTotaleDocumento)) {
    taxableTotal = taxableTotal.minus(roundingTotal);
    roundingStrategy = InoviceRoundingStrategy.SUBTRACT;
  }

  return {
    taxableTotal: ensureDecimalPlaces(newMoney(taxableTotal.toFixed(), currency), 2),
    vatTotal: ensureDecimalPlaces(newMoney(vatTotal.toFixed(), currency), 2),
    grossTotal: ensureDecimalPlaces(newMoney(taxableTotal.plus(vatTotal).toFixed(), currency), 2),
    roundingStrategy,
  };
};
